<template>
    <section class="dash">
        <!-- Encabezado de página -->
        <div class="dash-header">
            <div>
                <h1 class="dash-title">Panel de control</h1>
                <p class="dash-subtitle">Resumen de miembros y asistencias en tiempo real</p>
            </div>
            <div class="dash-date">
                <b-icon icon="calendar-outline" size="is-small"></b-icon>
                {{ fechaHoy }}
            </div>
        </div>

        <!-- KPIs -->
        <div class="kpi-grid">
            <div class="kpi-card" v-for="(k, i) in kpis" :key="i">
                <div class="kpi-info">
                    <span class="kpi-label">{{ k.label }}</span>
                    <span class="kpi-value">{{ k.valor }}</span>
                    <span class="kpi-caption">{{ k.caption }}</span>
                </div>
                <div class="kpi-icon" :class="k.tono">
                    <b-icon :icon="k.icono" pack="mdi"></b-icon>
                </div>
            </div>
        </div>

        <!-- Banner de alerta -->
        <div v-if="alerta" class="alert-banner" :class="alerta.tipo">
            <div class="alert-icon">
                <b-icon :icon="alerta.icono" pack="mdi"></b-icon>
            </div>
            <div class="alert-text">
                <strong>{{ alerta.titulo }}</strong>
                <span>{{ alerta.detalle }}</span>
            </div>
            <span v-if="alerta.contador" class="alert-count">{{ alerta.contador }}</span>
        </div>

        <!-- Gráfica + estado -->
        <div class="dash-grid">
            <div class="box chart-card">
                <div class="card-head">
                    <div>
                        <h3 class="card-title">Asistencias de la semana</h3>
                        <p class="card-sub">Presentes y ausentes por día</p>
                    </div>
                    <div class="legend">
                        <span class="legend-chip"><i class="dot dot-violet"></i> Presentes</span>
                        <span class="legend-chip"><i class="dot dot-green"></i> Ausentes</span>
                    </div>
                </div>
                <div class="chart-wrap">
                    <canvas id="grafica-semana"></canvas>
                </div>
            </div>

            <div class="box status-card">
                <h3 class="card-title">Estado del sistema</h3>
                <p class="card-sub">Servicios en línea</p>
                <ul class="status-list">
                    <li>
                        <span class="status-name">API del sistema</span>
                        <span class="status-pill" :class="estadoApi ? 'ok' : 'bad'">
                            <i class="dot"></i> {{ estadoApi ? 'Conectado' : 'Sin conexión' }}
                        </span>
                    </li>
                    <li>
                        <span class="status-name">Base de datos</span>
                        <span class="status-pill" :class="estadoApi ? 'ok' : 'bad'">
                            <i class="dot"></i> {{ estadoApi ? 'Conectada' : 'Sin conexión' }}
                        </span>
                    </li>
                    <li>
                        <span class="status-name">Sesión</span>
                        <span class="status-pill ok"><i class="dot"></i> Activa</span>
                    </li>
                    <li>
                        <span class="status-name">Grupo en contexto</span>
                        <span class="status-pill" :class="grupoNombre ? 'ok' : 'warn'">
                            <i class="dot"></i> {{ grupoNombre || 'No seleccionado' }}
                        </span>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Tabla de asistencias de hoy -->
        <div class="box table-card">
            <div class="card-head">
                <div>
                    <h3 class="card-title">Asistencias registradas hoy</h3>
                    <p class="card-sub">Últimos movimientos del grupo activo</p>
                </div>
            </div>
            <div class="table-wrap" v-if="asistenciasHoyAgrupadas.length">
                <table class="table is-fullwidth">
                    <thead>
                        <tr>
                            <th>Miembro</th>
                            <th>Oraciones a las que asistió</th>
                            <th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in asistenciasHoyAgrupadas.slice(0, 8)" :key="a.miembroId">
                            <td><strong>{{ a.miembroNombre }}</strong></td>
                            <td>
                                <span v-for="s in a.servicios" :key="s" class="tag is-light asis-tag">{{ s }}</span>
                            </td>
                            <td>
                                <span class="tag is-success">Presente</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p v-else class="empty-note">No hay asistencias registradas para hoy.</p>
        </div>
    </section>
</template>

<script>
    import HttpService from '../Servicios/HttpService'
    import Chart from 'chart.js'
    import AyudanteSesion from '../Servicios/AyudanteSesion'

    const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']

    function fechaISO(d){
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${dd}`
    }

    export default{
        name: "InicioComponent",
        data:()=>({
            miembros: [],
            grupos: [],
            asistenciasHoy: [],
            serviciosHoy: [],
            semana: { labels: [], presentes: [], ausentes: [] },
            resumenCenso: {},
            estadoApi: false,
            esSuper: false,
            grupoNombre: ''
        }),

        computed: {
            fechaHoy(){
                try{
                    const s = new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date())
                    return s.charAt(0).toUpperCase() + s.slice(1)
                }catch(e){ return '' }
            },
            presentesHoy(){
                // personas distintas que asistieron hoy (no filas/horarios)
                const set = {}
                this.asistenciasHoy.forEach(a => { if(String(a.presente) === '1') set[a.miembroId] = true })
                return Object.keys(set).length
            },
            ausentesHoy(){
                // ausente = miembro que no asistió a nada hoy; sólo cuenta si ya se tomó asistencia
                if(!this.asistenciasHoy.length) return 0
                return Math.max(0, this.miembros.length - this.presentesHoy)
            },
            asistenciasHoyPresentes(){
                // solo asistencias reales (presente=1); los desmarcados quedan como ausentes, no como registro
                return this.asistenciasHoy.filter(a => String(a.presente) === '1')
            },
            asistenciasHoyAgrupadas(){
                // una sola fila por miembro: junta todos los horarios a los que asistió hoy
                const mapa = {}
                const ordenadas = this.asistenciasHoyPresentes.slice().sort((x, y) => String(x.hora || '').localeCompare(String(y.hora || '')))
                ordenadas.forEach(a => {
                    const k = a.miembroId
                    if(!mapa[k]) mapa[k] = { miembroId: k, miembroNombre: a.miembroNombre, servicios: [] }
                    const etiqueta = `${a.servicioNombre || ''} ${a.hora || ''}`.trim()
                    if(mapa[k].servicios.indexOf(etiqueta) === -1) mapa[k].servicios.push(etiqueta)
                })
                return Object.keys(mapa).map(k => mapa[k])
            },
            marcadosHoy(){
                const set = {}
                this.asistenciasHoy.forEach(a => { set[a.miembroId] = true })
                return Object.keys(set).length
            },
            kpis(){
                const lista = [
                    { label: 'Total miembros', valor: this.miembros.length, caption: 'Registrados en el grupo', icono: 'account-group', tono: 'violet' },
                    { label: 'Asistencias hoy', valor: this.presentesHoy, caption: 'Marcados como presentes', icono: 'calendar-check', tono: 'green' },
                    { label: 'Ausentes hoy', valor: this.ausentesHoy, caption: 'Sin asistencia registrada hoy', icono: 'account-cancel', tono: 'amber' },
                    { label: 'Oraciones hoy', valor: this.serviciosHoy.length, caption: 'Programadas para hoy', icono: 'clock-outline', tono: 'blue' }
                ]
                // el censo es solo del SUPER en vista global — sin grupo en contexto
                if(this.esSuper && !localStorage.getItem('grupoSeleccionado') && !AyudanteSesion.actuandoComoGrupo()){
                    lista.splice(1, 0, { label: 'Familias', valor: this.resumenCenso.total_familias || 0, caption: `${this.resumenCenso.miembros_sin_familia || 0} miembro(s) sin familia`, icono: 'home-group', tono: 'pink' })
                }
                if(this.esSuper) lista.push({ label: 'Grupos', valor: this.grupos.length, caption: 'Congregaciones activas', icono: 'office-building', tono: 'pink' })
                return lista
            },
            alerta(){
                if(this.esSuper && !localStorage.getItem('grupoSeleccionado')){
                    return {
                        tipo: 'amber',
                        icono: 'account-group',
                        titulo: 'No hay un grupo seleccionado',
                        detalle: 'Ve a Administración > Seleccionar grupo para ver asistencias por congregación.',
                        contador: 0
                    }
                }
                if(this.serviciosHoy.length && this.miembros.length){
                    const sinMarcar = Math.max(0, this.miembros.length - this.marcadosHoy)
                    if(sinMarcar > 0){
                        return {
                            tipo: 'red',
                            icono: 'alert-circle',
                            titulo: `${sinMarcar} miembro(s) sin marcar asistencia`,
                            detalle: 'Aún no tienen registro en los servicios de hoy.',
                            contador: sinMarcar
                        }
                    }
                }
                return null
            }
        },

        mounted(){
            this.esSuper = AyudanteSesion.esSuper()
            this.resolverGrupoNombre()
            this.cargarTodo()
            this._onGrupo = () => {
                this.esSuper = AyudanteSesion.esSuper()
                this.resolverGrupoNombre()
                this.cargarTodo()
            }
            window.addEventListener('grupoSeleccionadoChanged', this._onGrupo)
        },

        beforeDestroy(){
            window.removeEventListener('grupoSeleccionadoChanged', this._onGrupo)
        },

        methods: {
            resolverGrupoNombre(){
                const ses = AyudanteSesion.obtenerDatosSesion()
                this.grupoNombre = localStorage.getItem('grupoSeleccionadoNombre') || (ses && ses.grupoNombre) || ''
                if(this.grupoNombre) return
                // sesiones anteriores a que el login devolviera grupoNombre/grupoId: resolver por API
                const pintar = (gid) => {
                    if(!gid) return
                    HttpService.obtenerConConsultas('grupos.php', { accion: 'obtener_por_id', id: Number(gid) })
                    .then(g => { if(g && g.nombre) this.grupoNombre = g.nombre })
                    .catch(() => {})
                }
                if(ses && ses.grupoId){ pintar(ses.grupoId); return }
                if(!this.esSuper && ses && ses.id){
                    HttpService.obtenerConConsultas('usuarios.php', { accion: 'obtener_por_id', id: Number(ses.id) })
                    .then(u => { if(u && u.grupoId) pintar(u.grupoId) })
                    .catch(() => {})
                }
            },
            payloadExtra(){
                const p = {}
                try{
                    const g = localStorage.getItem('grupoSeleccionado')
                    if(localStorage.getItem('superActuandoComoGrupo') && g) p.grupoSeleccionado = Number(g)
                    else if(this.esSuper && g) p.grupoSeleccionado = Number(g)
                }catch(e){ void e }
                return p
            },

            async cargarTodo(){
                this.cargarGrupos()
                this.cargarServiciosHoy()
                this.cargarCenso()
                await this.cargarMiembros()
                await this.cargarAsistencias()
                this.dibujarSemana()
            },

            cargarCenso(){
                // el censo solo se muestra al SUPER en vista global (sin grupo en contexto)
                if(!this.esSuper || localStorage.getItem('grupoSeleccionado') || AyudanteSesion.actuandoComoGrupo()){
                    this.resumenCenso = {}
                    return
                }
                HttpService.obtenerConConsultas('familias.php', Object.assign({ accion: 'obtener_completo' }, this.payloadExtra()))
                .then(r => { this.resumenCenso = (r && r.resumen) ? r.resumen : {} })
                .catch(() => { this.resumenCenso = {} })
            },

            cargarMiembros(){
                return HttpService.obtenerConConsultas('miembros.php', Object.assign({ accion: 'obtener' }, this.payloadExtra()))
                .then(r => {
                    this.miembros = Array.isArray(r) ? r : []
                    this.estadoApi = Array.isArray(r)
                })
                .catch(() => { this.estadoApi = false })
            },

            cargarGrupos(){
                if(!this.esSuper) return
                HttpService.obtenerConConsultas('grupos.php', { accion: 'obtener' })
                .then(r => { this.grupos = Array.isArray(r) ? r : [] })
                .catch(() => {})
            },

            cargarServiciosHoy(){
                HttpService.obtenerConConsultas('asistencias.php', Object.assign({ accion: 'servicios_por_fecha', fecha: fechaISO(new Date()) }, this.payloadExtra()))
                .then(r => { this.serviciosHoy = Array.isArray(r) ? r : [] })
                .catch(() => { this.serviciosHoy = [] })
            },

            async cargarAsistencias(){
                const hoy = new Date()
                const fechas = []
                for(let i = 6; i >= 0; i--){
                    const d = new Date(hoy)
                    d.setDate(hoy.getDate() - i)
                    fechas.push(d)
                }

                const resultados = await Promise.all(fechas.map(d =>
                    HttpService.obtenerConConsultas('asistencias.php', Object.assign({ accion: 'obtener', fecha: fechaISO(d) }, this.payloadExtra()))
                    .catch(() => [])
                ))

                const labels = []
                const presentes = []
                const ausentes = []
                resultados.forEach((r, i) => {
                    const rows = Array.isArray(r) ? r : []
                    // personas distintas que asistieron ese día; ausentes = miembros sin asistencia
                    const presSet = {}
                    rows.forEach(a => { if(String(a.presente) === '1') presSet[a.miembroId] = true })
                    const presDia = Object.keys(presSet).length
                    labels.push(DIAS[fechas[i].getDay()] + ' ' + fechas[i].getDate())
                    presentes.push(presDia)
                    ausentes.push(rows.length ? Math.max(0, this.miembros.length - presDia) : 0)
                })

                // último día = hoy
                this.asistenciasHoy = Array.isArray(resultados[resultados.length - 1]) ? resultados[resultados.length - 1] : []
                this.semana = { labels, presentes, ausentes }
            },

            dibujarSemana(){
                this.$nextTick(() => {
                    const viejo = document.getElementById('grafica-semana')
                    if(!viejo) return
                    const cont = viejo.parentNode
                    const nuevo = document.createElement('canvas')
                    nuevo.setAttribute('id', 'grafica-semana')
                    cont.replaceChild(nuevo, viejo)

                    const ctx = nuevo.getContext('2d')
                    const gViolet = ctx.createLinearGradient(0, 0, 0, 280)
                    gViolet.addColorStop(0, 'rgba(124, 92, 255, .30)')
                    gViolet.addColorStop(1, 'rgba(124, 92, 255, .02)')
                    const gGreen = ctx.createLinearGradient(0, 0, 0, 280)
                    gGreen.addColorStop(0, 'rgba(16, 185, 129, .22)')
                    gGreen.addColorStop(1, 'rgba(16, 185, 129, .02)')

                    new Chart(ctx, {
                        type: 'line',
                        data: {
                            labels: this.semana.labels,
                            datasets: [
                                { label: 'Presentes', data: this.semana.presentes, borderColor: '#7c5cff', backgroundColor: gViolet, fill: true, tension: .4, borderWidth: 2.5, pointRadius: 0, pointHoverRadius: 4, pointBackgroundColor: '#7c5cff' },
                                { label: 'Ausentes', data: this.semana.ausentes, borderColor: '#10b981', backgroundColor: gGreen, fill: true, tension: .4, borderWidth: 2.5, pointRadius: 0, pointHoverRadius: 4, pointBackgroundColor: '#10b981' }
                            ]
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            legend: { display: false },
                            tooltips: { mode: 'index', intersect: false },
                            scales: {
                                yAxes: [{ ticks: { beginAtZero: true, precision: 0, fontColor: '#8a90a6' }, gridLines: { color: '#eef0f7' } }],
                                xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#8a90a6' } }]
                            }
                        }
                    })
                })
            }
        }
    }
</script>

<style scoped>
.dash{ padding: .25rem 0 2rem; }

/* Encabezado */
.dash-header{
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
}
.dash-title{ font-size: 1.55rem; font-weight: 800; letter-spacing: -.02em; color: #1b1f2e; margin: 0; }
.dash-subtitle{ color: #8a90a6; font-size: .87rem; margin-top: .15rem; }
.dash-date{
    display: flex; align-items: center; gap: .4rem;
    color: #6b7186; font-size: .82rem; font-weight: 500;
    background: #fff; border: 1px solid #e9ecf5; border-radius: 10px;
    padding: .5rem .8rem; white-space: nowrap;
}
.dash-date .icon{ color: #8a90a6; }

/* KPIs */
.kpi-grid{
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 1rem;
    margin-bottom: 1.15rem;
}
.kpi-card{
    background: #fff;
    border: 1px solid #e9ecf5;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 8px 24px -14px rgba(16,24,40,.10);
    padding: 1.05rem 1.15rem;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: .75rem;
}
.kpi-info{ display: flex; flex-direction: column; min-width: 0; }
.kpi-label{ font-size: .76rem; font-weight: 600; color: #6b7186; }
.kpi-value{ font-size: 1.7rem; font-weight: 800; color: #1b1f2e; letter-spacing: -.02em; line-height: 1.2; }
.kpi-caption{ font-size: .72rem; color: #9aa0b8; margin-top: .1rem; }
.kpi-icon{
    width: 40px; height: 40px; border-radius: 11px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
}
.kpi-icon .icon{ font-size: 1.15rem; }
.kpi-icon.violet{ background: #efecff; color: #6d4aff; }
.kpi-icon.green{ background: #e7f8f1; color: #10b981; }
.kpi-icon.amber{ background: #fef4e4; color: #f59e0b; }
.kpi-icon.blue{ background: #eaf1ff; color: #4f7cff; }
.kpi-icon.pink{ background: #fdeef1; color: #f43f5e; }

/* Banner de alerta */
.alert-banner{
    display: flex; align-items: center; gap: .85rem;
    border-radius: 13px; padding: .9rem 1.1rem; margin-bottom: 1.15rem;
    border: 1px solid;
}
.alert-banner.red{ background: #fdeef1; border-color: #fbd3dc; }
.alert-banner.amber{ background: #fef4e4; border-color: #fbe3bb; }
.alert-icon{
    width: 38px; height: 38px; border-radius: 999px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
}
.alert-banner.red .alert-icon{ background: #f43f5e; color: #fff; }
.alert-banner.amber .alert-icon{ background: #f59e0b; color: #fff; }
.alert-text{ display: flex; flex-direction: column; flex: 1; line-height: 1.35; }
.alert-text strong{ font-size: .9rem; color: #3d2430; }
.alert-banner.amber .alert-text strong{ color: #6b4a12; }
.alert-text span{ font-size: .8rem; color: #8d6270; }
.alert-banner.amber .alert-text span{ color: #97783d; }
.alert-count{
    background: #f43f5e; color: #fff; font-weight: 800; font-size: .8rem;
    border-radius: 999px; min-width: 30px; height: 30px; padding: 0 .5rem;
    display: flex; align-items: center; justify-content: center;
}

/* Grid gráfica + estado */
.dash-grid{
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 1.15rem;
    margin-bottom: 1.15rem;
}
.card-head{
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: .75rem; margin-bottom: .85rem;
}
.card-title{ font-size: 1rem; font-weight: 700; color: #1b1f2e; margin: 0; }
.card-sub{ font-size: .78rem; color: #8a90a6; margin-top: .15rem; }
.legend{ display: flex; gap: .5rem; }
.legend-chip{
    display: inline-flex; align-items: center; gap: .35rem;
    font-size: .72rem; font-weight: 600; color: #6b7186;
    background: #f6f7fd; border: 1px solid #eceef8;
    border-radius: 8px; padding: .25rem .55rem;
}
.dot{ width: 8px; height: 8px; border-radius: 999px; display: inline-block; }
.dot-violet{ background: #7c5cff; }
.dot-green{ background: #10b981; }
.chart-wrap{ height: 290px; position: relative; }
.chart-wrap canvas{ width: 100% !important; height: 100% !important; }

/* Estado del sistema */
.status-list{ list-style: none; margin: .25rem 0 0; padding: 0; }
.status-list li{
    display: flex; align-items: center; justify-content: space-between;
    padding: .7rem 0; border-bottom: 1px solid #f1f3f9;
}
.status-list li:last-child{ border-bottom: 0; }
.status-name{ font-size: .85rem; color: #4b5267; font-weight: 500; }
.status-pill{
    display: inline-flex; align-items: center; gap: .4rem;
    font-size: .72rem; font-weight: 600;
    border-radius: 999px; padding: .25rem .65rem;
}
.status-pill.ok{ background: #e7f8f1; color: #0a8f68; }
.status-pill.ok .dot{ background: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,.15); }
.status-pill.warn{ background: #fef4e4; color: #b45309; }
.status-pill.warn .dot{ background: #f59e0b; }
.status-pill.bad{ background: #fdeef1; color: #e11d48; }
.status-pill.bad .dot{ background: #f43f5e; }

/* Tabla */
.table-card{ margin-bottom: .5rem; }
.table-wrap{ overflow-x: auto; }
.empty-note{ color: #8a90a6; font-size: .85rem; padding: 1rem .25rem; }
.asis-tag{ margin-right: .35rem; margin-bottom: .15rem; font-weight: 600; color: #3730a3; background: #eef2ff; }

@media (max-width: 900px){
    .dash-grid{ grid-template-columns: 1fr; }
    .dash-header{ flex-direction: column; }
}

@media (max-width: 768px){
    .kpi-grid{ grid-template-columns: repeat(2, 1fr); gap: .65rem; }
    .kpi-card{ padding: .8rem .85rem; }
    .kpi-value{ font-size: 1.35rem; }
    .kpi-caption{ font-size: .68rem; }
    .kpi-icon{ width: 34px; height: 34px; border-radius: 9px; }
    .chart-wrap{ height: 220px; }
    .dash-date{ align-self: flex-start; }
    .legend{ flex-wrap: wrap; }
}

@media (max-width: 380px){
    .kpi-grid{ grid-template-columns: 1fr; }
}
</style>
